import is from '@sindresorhus/is';
import { z } from 'zod';

import type {
    AiTransformationAttributeFilter,
    AiTransformationMatrixGrouping,
    AiTransformationMatrixResponse,
    AiTransformationSubject,
} from '../../../data-contract/ai-transformation-strategy';
import { automationScoreLevelSchema } from '../../../data-contract/ai-transformation-strategy';
import type {
    AiTransformationFilters,
    AiTransformationPickBounds,
} from '../../../fetchers/position-management/ai-transformation-fetchers';

export const STRATEGY_VIEWS = ['roles', 'levels', 'positions', 'tasks'] as const;
export type StrategyView = (typeof STRATEGY_VIEWS)[number];

export const STRATEGY_LAYOUTS = ['kanban', 'list'] as const;
export type StrategyLayout = (typeof STRATEGY_LAYOUTS)[number];

/** What the matrix plots upwards: the business impact or the weekly hours, in the toolbar's order. */
export const STRATEGY_Y_AXES = ['impact', 'hours'] as const;
export type StrategyYAxis = (typeof STRATEGY_Y_AXES)[number];

/** A rectangle picked on the matrix; it always belongs to the view's grouping and the matrix's y-axis. */
export type StrategyPick = AiTransformationPickBounds;

/**
 * Everything the page shows, read from and written to the URL. The selected options of every attribute filter share
 * one list, as the backend reads them. The list layout shows the opportunities, the matrix and the list under it; the
 * kanban layout shows the progress board.
 */
export type StrategyViewState = Pick<
    AiTransformationFilters,
    'attributeValueId' | 'jobClusterId' | 'jobFamilyId' | 'levelId' | 'q' | 'roleId' | 'scoreLevel'
> & {
    readonly architectureId: string | undefined;
    readonly layout: StrategyLayout;
    readonly pick: StrategyPick | undefined;
    readonly view: StrategyView;
    readonly yAxis: StrategyYAxis;
};

const DEFAULT_VIEW: StrategyView = 'roles';
const DEFAULT_LAYOUT: StrategyLayout = 'list';
const DEFAULT_Y_AXIS: StrategyYAxis = 'impact';
const MAX_QUERY_LENGTH = 200;

export const DEFAULT_STRATEGY_VIEW_STATE: StrategyViewState = {
    architectureId: undefined,
    attributeValueId: [],
    jobClusterId: [],
    jobFamilyId: [],
    layout: DEFAULT_LAYOUT,
    levelId: [],
    pick: undefined,
    q: undefined,
    roleId: [],
    scoreLevel: [],
    view: DEFAULT_VIEW,
    yAxis: DEFAULT_Y_AXIS,
};

export const STRATEGY_SEARCH_PARAMS = [
    'architectureId',
    'attributeValueId',
    'jobClusterId',
    'jobFamilyId',
    'layout',
    'levelId',
    'pickHoursMax',
    'pickHoursMin',
    'pickImpactMax',
    'pickImpactMin',
    'pickPeopleShareMax',
    'pickPeopleShareMin',
    'pickScoreMax',
    'pickScoreMin',
    'q',
    'roleId',
    'scoreLevel',
    'view',
    'yAxis',
] as const;

const viewSchema = z.enum(STRATEGY_VIEWS).catch(DEFAULT_VIEW);
const layoutSchema = z.enum(STRATEGY_LAYOUTS).catch(DEFAULT_LAYOUT);
const yAxisSchema = z.enum(STRATEGY_Y_AXES).catch(DEFAULT_Y_AXIS);
const shareSchema = z.coerce.number().min(0).max(1);
const hoursSchema = z.coerce.number().min(0);
const scoreBoundsShape = { scoreMax: shareSchema, scoreMin: shareSchema };
const pickSchema = z.union([
    z
        .object({ ...scoreBoundsShape, hoursMax: hoursSchema, hoursMin: hoursSchema })
        .refine(({ hoursMax, hoursMin, scoreMax, scoreMin }) => scoreMin <= scoreMax && hoursMin <= hoursMax),
    z
        .object({ ...scoreBoundsShape, impactMax: shareSchema, impactMin: shareSchema })
        .refine(({ impactMax, impactMin, scoreMax, scoreMin }) => scoreMin <= scoreMax && impactMin <= impactMax),
]);

const idsOf = (params: URLSearchParams, name: string): readonly string[] => [
    ...new Set(
        params
            .getAll(name)
            .map((value) => value.trim())
            .filter(is.nonEmptyString),
    ),
];

const textOf = (params: URLSearchParams, name: string, maxLength?: number): string | undefined => {
    const value = params.get(name)?.trim().slice(0, maxLength);

    return is.nonEmptyString(value) ? value : undefined;
};

/**
 * The score bounds and the bounds of one y-axis, the hours read first: a partial, out of range or inverted rectangle
 * reads as no pick.
 */
const pickOf = (params: URLSearchParams): StrategyPick | undefined =>
    pickSchema.safeParse({
        hoursMax: textOf(params, 'pickHoursMax'),
        hoursMin: textOf(params, 'pickHoursMin'),
        impactMax: textOf(params, 'pickImpactMax'),
        impactMin: textOf(params, 'pickImpactMin'),
        scoreMax: textOf(params, 'pickScoreMax'),
        scoreMin: textOf(params, 'pickScoreMin'),
    }).data;

/** The y-axis a pick was drawn on, which its bounds tell. */
const pickAxisOf = (pick: StrategyPick): StrategyYAxis => (is.number(pick.hoursMin) ? 'hours' : 'impact');

/**
 * Reads the page state from the URL; values the page does not know fall back to their defaults. A link from before the
 * impact became the default carries an hours pick without a y-axis, so it opens on the hours; a pick drawn on another
 * axis than the URL's is dropped, so the matrix never selects by an axis it does not show.
 */
export const parseStrategyViewState = (params: URLSearchParams): StrategyViewState => {
    const pick = pickOf(params);
    const yAxis = yAxisSchema.parse(params.get('yAxis') ?? (pick === undefined ? undefined : pickAxisOf(pick)));

    return {
        architectureId: textOf(params, 'architectureId'),
        attributeValueId: idsOf(params, 'attributeValueId'),
        jobClusterId: idsOf(params, 'jobClusterId'),
        jobFamilyId: idsOf(params, 'jobFamilyId'),
        layout: layoutSchema.parse(params.get('layout') ?? undefined),
        levelId: idsOf(params, 'levelId'),
        pick: pick !== undefined && pickAxisOf(pick) === yAxis ? pick : undefined,
        q: textOf(params, 'q', MAX_QUERY_LENGTH),
        roleId: idsOf(params, 'roleId'),
        scoreLevel: [...new Set(params.getAll('scoreLevel'))].flatMap(
            (value) => automationScoreLevelSchema.safeParse(value).data ?? [],
        ),
        view: viewSchema.parse(params.get('view') ?? undefined),
        yAxis,
    };
};

/** Writes the page state as search params, leaving out every value that equals its default. */
export const serializeStrategyViewState = (state: StrategyViewState): URLSearchParams => {
    const params = new URLSearchParams();
    const set = (name: string, value: string | undefined, defaultValue?: string): void => {
        if (value !== undefined && value !== defaultValue) params.set(name, value);
    };
    const append = (name: string, values: readonly string[]): void => {
        for (const value of values) params.append(name, value);
    };
    const setBound = (name: string, value: number | undefined): void => set(name, value?.toString());

    set('architectureId', state.architectureId);
    set('view', state.view, DEFAULT_VIEW);
    set('layout', state.layout, DEFAULT_LAYOUT);
    set('yAxis', state.yAxis, DEFAULT_Y_AXIS);
    append('jobFamilyId', state.jobFamilyId);
    append('jobClusterId', state.jobClusterId);
    append('roleId', state.roleId);
    append('levelId', state.levelId);
    append('attributeValueId', state.attributeValueId);
    append('scoreLevel', state.scoreLevel);
    set('q', state.q);
    setBound('pickScoreMin', state.pick?.scoreMin);
    setBound('pickScoreMax', state.pick?.scoreMax);
    setBound('pickHoursMin', state.pick?.hoursMin);
    setBound('pickHoursMax', state.pick?.hoursMax);
    setBound('pickImpactMin', state.pick?.impactMin);
    setBound('pickImpactMax', state.pick?.impactMax);

    return params;
};

const GROUPING_OF_VIEW: Readonly<Record<StrategyView, AiTransformationMatrixGrouping>> = {
    levels: 'LEVEL',
    positions: 'POSITION',
    roles: 'ROLE',
    tasks: 'TASK',
};

/** What the counts, board and list show: tasks in the tasks view, else positions. */
export const subjectOf = (view: StrategyView): AiTransformationSubject => (view === 'tasks' ? 'TASK' : 'POSITION');

export const groupingOf = (view: StrategyView): AiTransformationMatrixGrouping => GROUPING_OF_VIEW[view];

/** The y-axis the matrix plots: the chosen one, but the hours when the backend sends no impact axis, as older ones do. */
export const shownYAxis = (
    yAxis: StrategyYAxis,
    axes: AiTransformationMatrixResponse['axes'] | undefined,
): StrategyYAxis => (yAxis === 'impact' && axes?.impact !== undefined ? 'impact' : 'hours');

/** Whether the page needs the matrix axes: on the plot, and away from it while an impact pick needs checking. */
export const readsMatrixAxes = (state: Pick<StrategyViewState, 'pick'>, onPlot: boolean): boolean =>
    onPlot || is.number(state.pick?.impactMin);

/** Whether the state's pick, if any, was drawn on the y-axis the matrix plots with these axes. */
export const isPickShown = (
    { pick, yAxis }: Pick<StrategyViewState, 'pick' | 'yAxis'>,
    axes: AiTransformationMatrixResponse['axes'],
): boolean => pick === undefined || pickAxisOf(pick) === shownYAxis(yAxis, axes);

/** The page's filters without its pick, as the role and level tile lists that role's or level's positions. */
export const toQueryFiltersWithoutPick = ({
    attributeValueId,
    jobClusterId,
    jobFamilyId,
    levelId,
    q,
    roleId,
    scoreLevel,
}: StrategyViewState): AiTransformationFilters => ({
    attributeValueId,
    jobClusterId,
    jobFamilyId,
    levelId,
    q,
    roleId,
    scoreLevel,
});

const toQueryFilters = (state: StrategyViewState): AiTransformationFilters => {
    const filters = toQueryFiltersWithoutPick(state);

    return state.pick === undefined
        ? filters
        : { ...filters, pick: { ...state.pick, grouping: groupingOf(state.view) } };
};

/**
 * The inputs of every query on the page, all built on one filter object so they always show the same items. The pick
 * narrows the counts, the board and the list; the matrix gets it only to mark its picked points.
 */
export const strategyQueryInputs = (state: StrategyViewState) => {
    const filters = toQueryFilters(state);
    const subject = subjectOf(state.view);

    return {
        columns: { filters, subject },
        list: { filters, grouping: groupingOf(state.view), subject },
        matrix: { filters, grouping: groupingOf(state.view) },
        summary: { filters, subject },
    } as const;
};

/**
 * Merges a change into the state. A pick belongs to the view's grouping and the matrix's y-axis, so switching either
 * clears it.
 */
export const updateStrategyViewState = (
    state: StrategyViewState,
    change: Partial<StrategyViewState>,
): StrategyViewState => {
    const switchesView = change.view !== undefined && change.view !== state.view;
    const switchesYAxis = change.yAxis !== undefined && change.yAxis !== state.yAxis;

    return { ...state, ...(switchesView || switchesYAxis ? { pick: undefined } : {}), ...change };
};

/**
 * The selected attribute values the filters still list. The backend refuses a value of no listed attribute, so one
 * that is gone, as from an old link, would fail every read.
 */
export const listedAttributeValueIds = (
    ids: readonly string[],
    attributes: readonly AiTransformationAttributeFilter[],
): readonly string[] => {
    const listed = new Set(attributes.flatMap(({ options }) => options.map(({ id }) => id)));

    return ids.filter((id) => listed.has(id));
};
